import pluralize from 'pluralize';

import FacebookTargetingGeolocationCity from '@/descriptors/FacebookTargetingGeolocationCity';
import FacebookTargetingGeolocationCountry from '@/descriptors/FacebookTargetingGeolocationCountry';
import FacebookTargetingGeolocationCountryGroup from '@/descriptors/FacebookTargetingGeolocationCountryGroup';
import FacebookTargetingGeolocationCustomLocation from '@/descriptors/FacebookTargetingGeolocationCustomLocation';
import FacebookTargetingGeolocationPlace from '@/descriptors/FacebookTargetingGeolocationPlace';
import FacebookTargetingGeolocationRegion from '@/descriptors/FacebookTargetingGeolocationRegion';
import BaseEntity from '@/entities/BaseEntity';

export default class FacebookTargetingGeolocations extends BaseEntity {
  countries = [];
  country_groups = [];
  regions = [];
  cities = [];
  places = [];
  custom_locations = [];

  _entityArrayFields = {
    countries: FacebookTargetingGeolocationCountry,
    country_groups: FacebookTargetingGeolocationCountryGroup,
    regions: FacebookTargetingGeolocationRegion,
    cities: FacebookTargetingGeolocationCity,
    places: FacebookTargetingGeolocationPlace,
    custom_locations: FacebookTargetingGeolocationCustomLocation,
  };

  constructor(rawData) {
    super();
    this.updateWith(rawData);
  }

  add(type, key) {
    let className = null;
    switch (type) {
      case 'country':
        className = FacebookTargetingGeolocationCountry;
        break;

      case 'country_group':
        className = FacebookTargetingGeolocationCountryGroup;
        break;

      case 'region':
        className = FacebookTargetingGeolocationRegion;
        break;

      case 'city':
        className = FacebookTargetingGeolocationCity;
        break;

      case 'place':
        className = FacebookTargetingGeolocationPlace;
        break;
    }

    if (className === null) {
      throw new Error('Unkown ad geolocation type: ' + type);
    }

    const propertyName = pluralize(type);
    const dataset = this[propertyName];
    if (dataset.map(item => item.key).includes(key)) {
      return null;
    }

    const descriptorInstance = new className({ key });

    dataset.push(descriptorInstance);

    return descriptorInstance;
  }

  remove(type, key) {
    const propertyName = pluralize(type);
    this[propertyName] = this[propertyName].filter(item => item.key !== key);
  }

  clear() {
    Object.keys(this._entityArrayFields).forEach(property => this[property] = []);
  }

  get all() {
    const result = [
      ...this.countries,
      ...this.country_groups,
      ...this.regions,
      ...this.cities,
      ...this.places,
      ...this.custom_locations,
    ];

    return result;
  }

  get hasOverlaps() {
    for (const item of this.all) {
      // Two region groups overlap when their member countries intersect
      // (e.g. "Worldwide" contains "Europe"). A country contained in a group is
      // handled from the contained item's side below.
      if (item.type === 'country_group') {
        if (this._groupOverlapsAnotherGroup(item)) {
          return true;
        }
        continue;
      }

      if ('primary_city_id' in item.meta && this._isSelected('cities', item.meta.primary_city_id)) {
        return true;
      }
      if ('region_id' in item.meta && this._isSelected('regions', item.meta.region_id)) {
        return true;
      }
      if ('country_code' in item.meta && this._isSelected('countries', item.meta.country_code)) {
        return true;
      }

      // A selected country/region/city/place that falls inside a selected region
      // group overlaps it (e.g. "Europe" + "Budapest", or "Europe" + "Hungary").
      const itemCountry = item.type === 'country' ? item.key : item.meta.country_code;
      if (itemCountry && this._countryInSelectedGroup(itemCountry)) {
        return true;
      }
    }

    return false;
  }

  _isSelected(dataset, key) {
    for (const item of this[dataset]) {
      if (item.key == key) {
        return true;
      }
    }

    return false;
  }

  _countryInSelectedGroup(countryCode) {
    for (const group of this.country_groups) {
      if ((group.meta.country_codes || []).includes(countryCode)) {
        return true;
      }
    }

    return false;
  }

  _coveredCountryCodes() {
    const codes = new Set();

    this.countries.forEach(item => codes.add(item.key));
    this.country_groups.forEach(group => {
      (group.meta.country_codes || []).forEach(code => codes.add(code));
    });
    for (const item of [ ...this.regions, ...this.cities, ...this.places ]) {
      const countryCode = item.meta && item.meta.country_code;
      if (countryCode) {
        codes.add(countryCode);
      }
    }

    return codes;
  }

  _groupOverlapsAnotherGroup(group) {
    const codes = group.meta.country_codes || [];
    if (codes.length === 0) {
      return false;
    }

    for (const other of this.country_groups) {
      if (other.key === group.key) {
        continue;
      }

      const otherCodes = other.meta.country_codes || [];
      if (codes.some(code => otherCodes.includes(code))) {
        return true;
      }
    }

    return false;
  }

  parentSelected(item) {
    // Excluding a region group only makes sense if its member countries actually
    // overlap the included targeting (e.g. include "worldwide", exclude "europe").
    // It is meaningless when there is no overlap — Facebook's "europe" and "asia"
    // groups are disjoint, so excluding "asia" from an "europe" target covers
    // nothing and is rejected here.
    if (item.type === 'country_group') {
      const covered = this._coveredCountryCodes();
      return (item.meta.country_codes || []).some(code => covered.has(code));
    }

    // Any country/region/city/place is covered when it falls inside a selected
    // region group (e.g. exclude "Hungary" or "Budapest" while targeting "Europe").
    const itemCountry = item.type === 'country' ? item.key : item.meta.country_code;
    if (itemCountry && this._countryInSelectedGroup(itemCountry)) {
      return true;
    }

    if (item.type === 'country') {
      return false;
    }

    // A converted radius exclusion (custom_location) has no region_id/primary_city_id
    // — only a country_code — so it can only be located at country level. Treat it as
    // covered when its country is targeted directly, via a region group, or implied by
    // any included region/city/place (_coveredCountryCodes unions all of these). If the
    // country is unknown (older migrated coordinate that could not be resolved), don't
    // block the save: we cannot prove the exclusion is meaningless.
    if (item.type === 'custom_location') {
      const country = item.meta.country_code;
      if (!country) {
        return true;
      }

      return this._coveredCountryCodes().has(country);
    }

    if (item.type === 'place' || item.type === 'city' || item.type === 'region') {
      if (this.countries.map(item => item.key).includes(item.meta.country_code)) {
        return true;
      }
    }
    if (item.type === 'place' || item.type === 'city') {
      if (this.regions.map(item => item.key).includes(item.meta.region_id)) {
        return true;
      }
    }
    if (item.type === 'place') {
      if (this.cities.map(item => item.key).includes(item.meta.primary_city_id)) {
        return true;
      }
    }

    return false;
  }
}
